// API service for fetching daily numbers
const API_URL = process.env.REACT_APP_API_URL;

export interface NumbersData {
    numbers: number[];
    date: string;
}

export interface AllNumbersData {
    4: number[];
    5: number[];
    6: number[];
    date: string;
}

export interface CompletionStatus {
    [key: number]: boolean; // key is number count (4, 5, or 6)
}

// Fetch all numbers once (returns 4, 5, and 6 digit numbers)
export async function fetchAllNumbers(date?: string): Promise<AllNumbersData> {
    try {
        const url = `${API_URL}/api/GetNumbers${date ? `?date=${date}` : ""}`;
        const response = await fetch(url);

        if (!response.ok) {
            throw new Error(`Failed to fetch numbers: ${response.statusText}`);
        }

        const data = await response.json();

        // Convert each number to array of digits
        const fourDigits = data.four
            .toString()
            .split("")
            .map((d: string) => parseInt(d, 10));
        const fiveDigits = data.five
            .toString()
            .split("")
            .map((d: string) => parseInt(d, 10));
        const sixDigits = data.six
            .toString()
            .split("")
            .map((d: string) => parseInt(d, 10));

        return {
            4: fourDigits,
            5: fiveDigits,
            6: sixDigits,
            date: data.date,
        };
    } catch (error) {
        console.error("Error fetching numbers:", error);
        throw error;
    }
}

// Legacy function for backwards compatibility - extracts specific count from all numbers
export async function fetchNumbers(
    count: 4 | 5 | 6,
    date?: string
): Promise<NumbersData> {
    const allData = await fetchAllNumbers(date);
    return {
        numbers: allData[count],
        date: allData.date,
    };
}

// Save completion status to localStorage
export function saveCompletionStatus(date: string, count: number): void {
    const key = `completion_${date}`;
    const existing = getCompletionStatus(date);
    existing[count] = true;
    localStorage.setItem(key, JSON.stringify(existing));
}

// Get completion status from localStorage
export function getCompletionStatus(date: string): CompletionStatus {
    const key = `completion_${date}`;
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : {};
}

// Save successful expression to localStorage
export function saveSuccessfulExpression(
    date: string,
    count: number,
    expression: string
): void {
    const key = `expression_${date}_${count}`;
    localStorage.setItem(key, expression);
}

// Get successful expression from localStorage
export function getSuccessfulExpression(
    date: string,
    count: number
): string | null {
    const key = `expression_${date}_${count}`;
    return localStorage.getItem(key);
}

// Get today's date in YYYY-MM-DD format
export function getTodayDate(): string {
    const today = new Date();
    return today.toISOString().split("T")[0];
}
